import type { IEventSlot } from '@api/EventSlotsApi';
import type { ICmsComponentBlock, ICmsMedia, ResourceClassName } from '../types';
import {
	parseArticleIntro,
	parseCopytext,
	parseDetailHeader,
	parseEventTickets,
	parseFaq,
	parseHeadline,
	parseRelatedEvents,
	parseTextImageTeaser,
	parseImageTeaser
} from './parser';
import type { IEvent } from '@api/EventsApi';

export const MAPPABLE_RESOURCE_CLASSES: ResourceClassName[] = [
	'ArticleIntro',
	'DetailHeader',
	'EventTickets',
	'Faq',
	'Headline',
	'Copytext',
	'RelatedEvents',
	'TextImage',
	'ImageTeaser'
] as const;

export const RESOURCE_CLASSES_WITH_IMAGE: ResourceClassName[] = ['TextImage'] as const;

export function filterMappableResourceClass(
	cmsResources: ICmsComponentBlock[]
): ICmsComponentBlock[] {
	return cmsResources.filter((resource) => {
		return MAPPABLE_RESOURCE_CLASSES.includes(resource.resource_class_name);
	});
}

export function sortCmsResources(cmsResources: ICmsComponentBlock[]): ICmsComponentBlock[] {
	return cmsResources.sort((a, b) => {
		const sortKeyA = Number(a.sort_key);
		const sortKeyB = Number(b.sort_key);

		if (sortKeyA < sortKeyB) return -1;
		if (sortKeyA > sortKeyB) return 1;
		return 0;
	});
}

export function matchCmsMediaByImageId(
	imageId: string,
	cmsMediaBlocks: ICmsMedia[]
): ICmsMedia | undefined {
	return cmsMediaBlocks.find((media) => media.id === imageId);
}

export function sortAndFilterCmsResources(
	cmsResources: ICmsComponentBlock[]
): ICmsComponentBlock[] {
	return sortCmsResources(filterMappableResourceClass(cmsResources));
}

export const pageUsesCmsEventTicketsComponent = (cmsBlocks: ICmsComponentBlock[]): boolean =>
	cmsBlocks.some((block) => block.resource_class_name === 'EventTickets');

export function getComponentProps(data: {
	currentEvent: IEvent;
	additionalAggregatedEventData?: IEventSlot;
	cmsComponentBlock: ICmsComponentBlock;
	cmsMediaBlocks: ICmsMedia[];
	relatedEvents: IEventSlot[];
	usesCmsEventTicketsComponent: boolean;
	componentIndex: number;
}) {
	const {
		currentEvent,
		additionalAggregatedEventData,
		cmsComponentBlock,
		cmsMediaBlocks,
		relatedEvents,
		usesCmsEventTicketsComponent,
		componentIndex
	} = data;
	switch (cmsComponentBlock.resource_class_name) {
		case 'DetailHeader': {
			return parseDetailHeader(
				currentEvent,
				additionalAggregatedEventData,
				usesCmsEventTicketsComponent
			);
		}
		case 'ArticleIntro': {
			return parseArticleIntro(currentEvent);
		}
		case 'EventTickets': {
			return parseEventTickets(currentEvent);
		}
		case 'Headline': {
			return parseHeadline(cmsComponentBlock);
		}
		case 'Copytext': {
			return parseCopytext(cmsComponentBlock);
		}
		case 'TextImage': {
			return {
				...parseTextImageTeaser(cmsComponentBlock, cmsMediaBlocks),
				loading: componentIndex > 1 ? 'lazy' : 'eager'
			};
		}
		case 'ImageTeaser': {
			return {
				...parseImageTeaser(cmsComponentBlock, cmsMediaBlocks),
				loading: componentIndex > 1 ? 'lazy' : 'eager'
			};
		}
		case 'Faq': {
			return parseFaq(cmsComponentBlock);
		}
		case 'RelatedEvents': {
			return parseRelatedEvents(relatedEvents);
		}

		default:
			return {};
	}
}
